<template>
	<Box class="notification-box" data-testid="notification-box">
		<h3 data-testid="notification-box-title">{{ title }}</h3>

		<div v-if="items.length === 0" class="no-notifications" data-testid="no-notifications">
			{{ emptyMessage }}
		</div>

		<div v-else class="notifications-list" data-testid="notifications-list">
			<div v-for="(notification) in limitedItems" :key="notification.id" class="notification-item"
				:class="{ 'read': notification.is_read }" :data-testid="`notification-box-item-${notification.id}`">
				<div class="notification-icon" :class="{ 'check-icon': notification.type !== 'notification' }"
					:data-testid="`notification-box-icon-${notification.id}`">
					<BellOutline :size="19" v-if="notification.type === 'notification'" data-testid="bell-icon" />
					<Check :size="19" v-else data-testid="check-icon" />
				</div>

				<div class="notification-item-content" :data-testid="`notification-box-content-${notification.id}`">
					<a v-if="notification.link" :href="notification.link" class="item-title" :title="notification.link"
						:data-testid="`notification-box-title-${notification.id}`">{{
							notification.title }}</a>
					<strong v-else class="item-title" :data-testid="`notification-box-title-${notification.id}`">{{
						notification.title }}</strong>
					<p v-html="notification.description" :data-testid="`notification-box-description-${notification.id}`"></p>

					<div class="attachments" :data-testid="`notification-box-attachments-${notification.id}`">
						<div v-for="(attachment, attIndex) in notification.anexos" :key="attIndex" class="attachment-item"
							@click="exportAttchment(attachment)"
							:data-testid="`notification-box-attachment-${notification.id}-${attIndex}`">
							<File :size="15" />
							<span class="attachment-name">{{
								attachment.filename.split("-")[1]
							}}</span>
						</div>
					</div>

					<span class="time" :data-testid="`notification-box-time-${notification.id}`">{{
						formatDate(notification.created_at) }}</span>
				</div>

				<span v-if="!notification.is_read" class="dot" :data-testid="`notification-box-dot-${notification.id}`"></span>
			</div>
		</div>

		<div class="footer-actions" data-testid="footer-actions">
			<Button color="primary" :fill="false" @handle:click="goToNotifications" text="Ver todas"
				data-testid="view-all-btn">
				<BellOutline :size="19" />
			</Button>
		</div>
	</Box>
</template>

<script>
import Box from 'core/Box'
import Button from 'core/Button'
import File from 'icons/File'
import BellOutline from 'icons/BellOutline'
import Check from "icons/Check"
import utils from '../../services/utils'

export default {
	name: 'NotificationBox',
	components: {
		Box,
		Button,
		File,
		BellOutline,
		Check,
	},
	props: {
		title: {
			type: String,
			required: true,
		},
		items: {
			type: Array,
			default: () => [],
		},
		emptyMessage: {
			type: String,
			default: 'Nenhum item encontrado',
		},
		limit: {
			type: Number,
			default: 5,
		},
	},
	computed: {
		limitedItems() {
			return this.items.slice(0, this.limit)
		},
	},
	methods: {
		formatDate(date) {
			return utils.formatDate(date)
		},
		async exportAttchment(attachment) {
			const base64 = attachment.file

			if (!base64) {
				this.$toasted.show('Anexo não encontrado.', {
					icon: 'fa-times',
					position: 'top-right',
					duration: 5000,
					fullWidth: false,
      	})

				return
			}

			const filename = attachment.filename.split("-")[1]
			const mimetype = attachment.mimetype

			const byteCharacters = atob(base64)
			const byteNumbers = Array.from(byteCharacters, (char) =>
				char.charCodeAt(0)
			)
			const byteArray = new Uint8Array(byteNumbers)
			const blob = new Blob([byteArray], { type: mimetype })

			const link = document.createElement("a")
			link.href = URL.createObjectURL(blob)
			link.download = filename
			document.body.appendChild(link)
			link.click()
			document.body.removeChild(link)
			URL.revokeObjectURL(link.href)
		},
		goToNotifications() {
			if (this.title === 'Notificações') {
				this.$router.push({ name: 'notifications', query: { tab: 'notification' } })
			} else if (this.title === 'Controle de Versões') {
				this.$router.push({ name: 'notifications', query: { tab: 'version_control' } })
			}
		}
	},
}
</script>

<style lang="scss" scoped>
.notification-box {
	flex: 1;
	display: flex;
	flex-direction: column;
	min-width: 300px;

	h3 {
		margin: 0 0 1rem 0;
		color: #1C2D5A;
		font-size: 1.25rem;
		font-weight: 600;
	}

	.no-notifications {
		flex: 1;
		display: flex;
		justify-content: center;
		align-items: center;
		color: #6c757d;
		padding: 2rem;
		font-style: italic;
		font-size: 1.25rem;
	}

	.notifications-list {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 1rem;

		.notification-item {
			padding: 1rem;
			border: 1px solid #e0e0e0c1;
			border-radius: 14px;

			display: flex;
			align-items: flex-start;
			justify-content: space-between;
			gap: 1.25rem;

			.notification-icon {
				padding: 0.25rem 0.55rem 0.45rem;
				background-color: rgba(255, 224, 129, 0.4);
				border-radius: 10px;

				color: #ffc20e;

				&.check-icon {
					background-color: rgba(76, 175, 80, 0.2);
					color: #4caf50;
				}
			}

			.dot {
				display: inline-block;
				width: 8px;
				height: 8px;
				background-color: #ffc20e;
				border-radius: 50%;
			}

			.notification-item-content {
				flex: 1;
				display: flex;
				flex-direction: column;
				gap: 0.5rem;

				.item-title {
					margin: 0;
					font-size: 1rem;
					font-weight: 900;
					color: #1C2D5A;
				}

				p {
					margin: 0;
					font-size: 0.75rem;
					font-weight: 500;
					color: #202224;
					word-wrap: break-word;
					word-break: break-word;
					overflow-wrap: break-word;
				}

				span {
					font-size: 0.65rem;
					font-weight: 600;
					color: #202224;
				}
			}

			.attachments {
				display: flex;
				flex-direction: column;
				flex-wrap: wrap;
				gap: 0.25rem;

				.attachment-item {
					background-color: #3269ff28;
					border-radius: 6px;
					padding: 0.25rem 0.5rem;
					display: flex;
					align-items: center;
					gap: 0.5rem;
					font-size: 0.875rem;
					color: #202224;
					cursor: pointer;

					&:hover {
						background-color: #3269ff49;
					}
				}
			}
		}
	}

	.footer-actions {
		display: flex;
		justify-content: flex-end;
		padding-top: 1rem;
		margin-top: 1rem;
	}

	@media only screen and (max-width: 780px) {
		min-width: 100%;
	}
}
</style>